import { useCallback, useEffect, useRef, useState } from "react";
import { JOB_TYPE_LABELS, type Job, type PhotoSource } from "@eagledispatch/shared";
declare const BUILD_TAG: string;
import { apiFetch, ApiError, API_URL } from "../../lib/api";
import { queuedApiFetch } from "../../lib/offline-queue";
import Button from "../../components/Button";
import Card from "../../components/Card";

function snapshotFields(job: Job) {
  const snap = job.customerSnapshot as {
    customer?: Record<string, unknown>;
    property?: Record<string, unknown>;
  };
  return {
    customer: snap.customer ?? {},
    property: snap.property ?? {},
  };
}

function formatStartTime(isoString?: string | null): string {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

type LoadPhoto = {
  id: string;
  kind: string;
  photoSource: PhotoSource;
  uploadedAt: string;
  path: string;
};

/**
 * ActiveJob — the driver's assigned job home. Shows the frozen snapshot card +
 * dispatcher messages + address.
 *
 * Flow:
 * - NEW state: shows START JOB (primary green). Tap -> POST /jobs/:id/start ->
 *   screen STAYS on job info, button morphs to red FINISH JOB, and status line
 *   shows "In progress — started {time}".
 * - IN_PROGRESS state: shows FINISH JOB (danger red) + start time + LOAD PHOTOS.
 * - Tap FINISH JOB -> calls onFinish(currentJob) to navigate to photo capture.
 *
 * Unit #14: 30s poll on GET /jobs/:id. If the server shows state CANCELLED,
 * call `onCancelled` (parent routes to waiting + shows toast). If the
 * `driverId` is no longer the session driver's, call `onReassigned`. Unit #25:
 * if the job is PAUSED, call `onPaused` so the driver returns to waiting.
 * All callbacks fire at most once per mount (the unmount guard). Poll errors
 * are silent — slow-network tolerance (spec).
 *
 * Unit #26: LOAD PHOTOS section for pre-pickup documentation. Camera + gallery
 * capture, thumbnail grid, delete-own, and lightbox enlarge. Uploads are
 * idempotent per photo; the offline queue is NOT used here because it currently
 * JSON-stringifies bodies and cannot serialize multipart FormData (see related
 * issue in the phase report).
 */
export default function ActiveJob({
  job: initialJob,
  currentDriverId,
  onFinish,
  onCancelled,
  onReassigned,
  onPaused,
}: {
  job: Job;
  currentDriverId: string;
  onFinish: (job: Job) => void;
  onCancelled: () => void;
  onReassigned: () => void;
  onPaused?: () => void;
}) {
  const [job, setJob] = useState<Job>(initialJob);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  // Held idempotency key across retries.
  const startKeyRef = useRef<string | null>(null);
  // Monotonic load sequence — a slow poll response must never clobber a
  // newer state (POST start, POST finish, or a more recent poll).
  const loadSeqRef = useRef(0);

  // ── Unit #26: LOAD photo state ───────────────────────────────────────────
  const [loadPhotos, setLoadPhotos] = useState<LoadPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewRef = useRef<string | null>(null);
  const uploadKeyRef = useRef<string | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setJob(initialJob);
  }, [initialJob]);

  // Cleanup any preview blob URL on unmount.
  useEffect(() => {
    return () => {
      if (previewRef.current) {
        URL.revokeObjectURL(previewRef.current);
        previewRef.current = null;
      }
    };
  }, []);

  // ── Server change-detection poll (Unit #14 + Unit #25 PAUSED) ────────────
  // 30s interval. Detects three conditions the local screen has no way to
  // know about: CANCELLED, PAUSED, and reassigned. Each fires the
  // corresponding callback exactly once.
  useEffect(() => {
    let cancelled = false;
    let firedCancelled = false;
    let firedReassigned = false;
    let firedPaused = false;

    const POLL_MS = 30_000;

    const poll = async () => {
      const seq = ++loadSeqRef.current;
      try {
        const fresh = await apiFetch<Job>(`/api/v1/jobs/${initialJob.id}`);
        if (cancelled || seq !== loadSeqRef.current) return;
        // CANCELLED first — wins over a reassign that raced with the
        // cancellation (cancel sets driver_id NULL too, but the message
        // the driver sees should be "cancelled by dispatch" not
        // "reassigned").
        if (fresh.state === "CANCELLED" && !firedCancelled) {
          firedCancelled = true;
          onCancelled();
          return;
        }
        // Unit #25: PAUSED frees the driver for a priority job. The active-
        // job endpoint no longer returns it, so the screen must route back
        // to waiting just like a reassignment.
        if (fresh.state === "PAUSED" && !firedPaused) {
          firedPaused = true;
          if (onPaused) onPaused();
          return;
        }
        // Reassigned detection is null-inclusive. Reassign clears driver_id
        // to NULL — that's the very state the driver used to belong to this
        // job and no longer does.
        if (fresh.driverId !== currentDriverId && !firedReassigned) {
          firedReassigned = true;
          onReassigned();
          return;
        }
      } catch {
        // Silent — slow network must not disrupt the screen (spec).
      }
    };

    const id = window.setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      loadSeqRef.current += 1; // invalidate any in-flight poll
      window.clearInterval(id);
    };
  }, [initialJob.id, currentDriverId, onCancelled, onReassigned, onPaused]);

  // ── LOAD photos list (Unit #26) ──────────────────────────────────────────
  const fetchLoadPhotos = useCallback(async () => {
    try {
      const res = await apiFetch<{ photos: LoadPhoto[] }>(
        `/api/v1/jobs/${initialJob.id}/photos`,
      );
      setLoadPhotos(res.photos.filter((p) => p.kind === "LOAD"));
    } catch {
      // Keep existing list on transient failure; don't spam error state.
    }
  }, [initialJob.id]);

  useEffect(() => {
    void fetchLoadPhotos();
  }, [fetchLoadPhotos]);

  const start = async () => {
    setStarting(true);
    setError(null);
    setQueued(false);
    if (!startKeyRef.current) startKeyRef.current = crypto.randomUUID();
    try {
      // Per-attempt idempotency key held across retries. If offline, the
      // queue stores the SAME key so the replay dedups against this attempt.
      const res = await queuedApiFetch(`/api/v1/jobs/${job.id}/start`, {}, {
        idempotencyKey: startKeyRef.current,
        jobId: job.id,
        jobNumber: job.jobNumber,
        actionType: "START",
      });
      if (res.status === 202) {
        // Offline — action queued. Optimistic UI: proceed exactly as if 200.
        setQueued(true);
        const queuedJob: Job = {
          ...job,
          state: "IN_PROGRESS",
          startAt: new Date().toISOString(),
        };
        startKeyRef.current = null;
        setJob(queuedJob);
      } else {
        // Screen STAYS — update local job state to IN_PROGRESS and clear the key.
        startKeyRef.current = null;
        const started = (await res.json()) as Job;
        setJob(started);
      }
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setStarting(false);
    }
  };

  const { customer, property } = snapshotFields(job);

  const address = [
    property.addressLine1,
    property.city,
    property.state,
    property.zip,
  ]
    .filter(Boolean)
    .join(", ");

  // ── LOAD photo upload (Unit #26) ────────────────────────────────────────
  const doUpload = useCallback(
    async (file: File, source: PhotoSource) => {
      if (job.state !== "IN_PROGRESS" && job.state !== "PAUSED") {
        setError("Load photos can only be added while the job is in progress or paused.");
        setUploading(false);
        return;
      }
      setUploading(true);
      setError(null);
      const key = uploadKeyRef.current ?? crypto.randomUUID();
      uploadKeyRef.current = key;
      try {
        const form = new FormData();
        form.append("photo", file);
        form.append("source", source);
        form.append("kind", "LOAD");
        await apiFetch<{ id: string }>(`/api/v1/jobs/${job.id}/photo`, {
          method: "POST",
          headers: { "Idempotency-Key": key },
          body: form,
        });
        // Success: clear the held key and preview, then refresh the grid.
        uploadKeyRef.current = null;
        if (previewRef.current) {
          URL.revokeObjectURL(previewRef.current);
          previewRef.current = null;
        }
        setPreviewUrl(null);
        await fetchLoadPhotos();
      } catch (e) {
        setError(
          e instanceof ApiError
            ? e.message
            : e instanceof Error
              ? e.message
              : String(e),
        );
      } finally {
        setUploading(false);
      }
    },
    [job.id, job.state, fetchLoadPhotos],
  );

  const pick = (source: PhotoSource, files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    // Retake-in-place: clear any previous preview before making the new one.
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    uploadKeyRef.current = null; // fresh key per pick attempt
    const url = URL.createObjectURL(file);
    previewRef.current = url;
    setPreviewUrl(url);
    setError(null);
    void doUpload(file, source);
  };

  const deletePhoto = async (photoId: string) => {
    if (!window.confirm("Delete this load photo?")) return;
    setDeletingId(photoId);
    setError(null);
    try {
      await apiFetch<void>(`/api/v1/jobs/${job.id}/photos/${photoId}`, {
        method: "DELETE",
      });
      await fetchLoadPhotos();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setDeletingId(null);
    }
  };

  const startTimeStr = formatStartTime(job.startAt);
  const isInProgress = job.state === "IN_PROGRESS";
  const canUploadLoad = (job.state === "IN_PROGRESS" || job.state === "PAUSED") && !uploading && !starting;

  return (
    <main className="surface driver-active">
      <header className="surface-header">
        <h1>{job.jobNumber}</h1>
        <div className="header-actions">
          <span className="muted build-stamp" title={`build ${BUILD_TAG}`}>
            {BUILD_TAG.slice(0, 7)}
          </span>
        </div>
      </header>

      <Card>
        <h2 className="job-company">{String(customer.companyName ?? "—")}</h2>
        <p className="job-address">{address || "—"}</p>
      </Card>

      <Card>
        <div className="job-facts">
          <div className="job-fact">
            <span className="job-fact-label">Size</span>
            <span className="job-fact-value">{job.dumpsterSize ? `${job.dumpsterSize.label} (${job.dumpsterSize.yards} yd)` : "—"}</span>
          </div>
          <div className="job-fact">
            <span className="job-fact-label">Type</span>
            <span className="job-fact-value">{JOB_TYPE_LABELS[job.jobType]}</span>
          </div>
          <div className="job-fact">
            <span className="job-fact-label">Truck</span>
            <span className="job-fact-value">
              {job.truck ? `T${job.truck.unitNumber}` : "—"}
            </span>
          </div>
        </div>
      </Card>

      {job.message ? (
        <Card className="message-card">
          <h2>Dispatcher Message</h2>
          <p className="job-message">{job.message}</p>
        </Card>
      ) : null}

      {isInProgress && startTimeStr && (
        <p className="muted job-progress-status" style={{ textAlign: "center", margin: "8px 0" }}>
          In progress — started {startTimeStr}
        </p>
      )}

      {/* ── LOAD PHOTOS (Unit #26) ─────────────────────────────────────── */}
      {isInProgress && (
        <Card className="photo-section">
          <h2>Load Photos</h2>

          {previewUrl && (
            <div className="photo-preview" style={{ marginBottom: "12px" }}>
              <img
                className="photo-thumb"
                src={previewUrl}
                alt="Load photo preview"
              />
            </div>
          )}

          {uploading && (
            <p className="muted" style={{ marginBottom: "12px" }}>
              Uploading…
            </p>
          )}

          <div className="photo-actions">
            <input
              ref={cameraRef}
              type="file"
              accept="image/jpeg,image/png"
              capture="environment"
              hidden
              onChange={(e) => pick("CAMERA", e.target.files)}
            />
            <input
              ref={galleryRef}
              type="file"
              accept="image/jpeg,image/png"
              hidden
              onChange={(e) => pick("GALLERY", e.target.files)}
            />
            <Button
              disabled={!canUploadLoad}
              onClick={() => cameraRef.current?.click()}
            >
              📷 Add Load Photo
            </Button>
            <Button
              variant="secondary"
              disabled={!canUploadLoad}
              onClick={() => galleryRef.current?.click()}
            >
              🖼 From Gallery
            </Button>
          </div>

          {loadPhotos.length > 0 && (
            <div
              className="load-photo-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                gap: "10px",
                marginTop: "16px",
              }}
            >
              {loadPhotos.map((photo) => (
                <div
                  key={photo.id}
                  style={{
                    position: "relative",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid var(--border)",
                  }}
                >
                  <img
                    src={`${API_URL}${photo.path}`}
                    alt="Load photo thumbnail"
                    className="photo-thumbnail"
                    style={{
                      width: "100%",
                      height: "96px",
                      objectFit: "cover",
                      cursor: "zoom-in",
                    }}
                    onClick={() => setLightboxUrl(`${API_URL}${photo.path}`)}
                  />
                  {photo.photoSource === "GALLERY" && (
                    <span
                      className="tag tag-red"
                      style={{
                        position: "absolute",
                        top: "4px",
                        left: "4px",
                        fontSize: "10px",
                        padding: "2px 6px",
                      }}
                    >
                      Gallery
                    </span>
                  )}
                  <button
                    type="button"
                    aria-label="Delete load photo"
                    disabled={deletingId === photo.id}
                    onClick={() => void deletePhoto(photo.id)}
                    style={{
                      position: "absolute",
                      top: "4px",
                      right: "4px",
                      background: "rgba(179, 38, 30, 0.9)",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "6px",
                      padding: "4px 6px",
                      fontSize: "12px",
                      fontWeight: 700,
                      cursor: "pointer",
                      lineHeight: 1,
                    }}
                  >
                    🗑
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {error && <p className="form-error">{error}</p>}

      {queued && (
        <p className="queued-chip" style={{ textAlign: "center", marginTop: "10px" }}>
          ⏳ Start queued — will sync when online
        </p>
      )}

      {!isInProgress ? (
        <Button
          className="send-button"
          disabled={starting}
          onClick={() => void start()}
        >
          {starting ? "Starting…" : "START JOB"}
        </Button>
      ) : (
        <Button
          variant="danger"
          className="send-button finish-job-button"
          onClick={() => onFinish(job)}
        >
          FINISH JOB
        </Button>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxUrl(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="lightbox-content">
            <button
              type="button"
              className="lightbox-close"
              onClick={() => setLightboxUrl(null)}
            >
              Close
            </button>
            <img
              src={lightboxUrl}
              alt="Load photo enlarged"
              style={{ maxWidth: "90vw", maxHeight: "85vh" }}
            />
          </div>
        </div>
      )}
    </main>
  );
}
